import { DialogContent, TextField, SxProps } from '@mui/material';
import { DateTime } from 'luxon';
import React, { useCallback, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import DateInput from './inputs/DateInput';
import { IProjectInfo } from '@shared/models/project.model';
import * as Yup from 'yup';
import { createYupResolver } from '@client/helpers/form-validation.helpers';
import EditProjectActions from './EditProjectActions';
import { useComponentUUID } from '@client/hooks/use-utils.hooks';
import {
  useSubmitProjectData,
  useNewProjectName,
  useFindDuplicateNameTemplateProjectId,
  useConfirmReplaceTemplate,
} from './edit-project.hooks';
import {
  DialogType,
  EditProjectFormData,
  ProjectType,
  useEditProjectDialogStore,
} from './edit-project.store';
import { is } from '@client/helpers/dom.guards';
import NameInput from './inputs/NameInput';
import { useProjectState } from '@client/store/project';
import { usePromiseSnackbar } from '@client/hooks/use-snackbar.hooks';

type EditProjectFormProps = {
  mode: DialogType;
  type: ProjectType;
  project?: IProjectInfo;
  onClose: () => void;
  /**
   * True from the start of submit until it finishes (including navigate after
   * create), so the dialog can block X / Escape the same way Cancel is blocked.
   */
  onSubmitBusyChange: (busy: boolean) => void;
};

const EditProjectForm: React.FC<EditProjectFormProps> = ({
  mode,
  type,
  project,
  onClose,
  onSubmitBusyChange,
}) => {
  const { fetchProjects } = useProjectState('fetchProjects');
  const findDuplicateNameTemplateId = useFindDuplicateNameTemplateProjectId();
  const confirmReplaceTemplate = useConfirmReplaceTemplate();
  const promiseSnackbar = usePromiseSnackbar();
  const submitProjectData = useSubmitProjectData(mode, type);
  // A fresh name each mount, plus autoComplete="off", stops the browser from
  // matching values previously typed into this field.
  const generatedDescriptionName = useComponentUUID();

  const { name, description, completed_at } = project ?? {};
  const newProjectName = useNewProjectName(type, mode, name);

  const defaultValues = useMemo<EditProjectFormData>(
    () => ({
      description: description ?? '',
      completed_at: completed_at ? DateTime.fromISO(completed_at) : null,
      name: newProjectName,
    }),
    [description, completed_at, newProjectName],
  );

  const focusField = useEditProjectDialogStore((s) => s.focusField);

  const {
    control,
    handleSubmit,
    formState: { dirtyFields, errors, isReady, isSubmitting },
  } = useForm<EditProjectFormData>({
    defaultValues,
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: createYupResolver<EditProjectFormData>(schema, ['name']),
  });

  const isValid = isReady && Object.keys(errors).length === 0;

  const isSubmittable =
    mode === 'edit' ? isValid && Object.keys(dirtyFields).length > 0 : isValid;

  const submit = useCallback(
    async (data: EditProjectFormData) => {
      if (!isSubmittable) {
        return;
      }

      onSubmitBusyChange(true);
      try {
        // Stay mounted until create has navigated — unmounting first left the
        // user on the project list when navigation ran after the dialog closed.
        if (type === 'project') {
          await promiseSnackbar(submitProjectData(data));
          onClose();
          return;
        }

        if (!project) return;

        const duplicateNameTemplateId = findDuplicateNameTemplateId(
          data.name,
          project.id,
        );

        // if a template with the same name exists, prompt user to confirm replacement of existing template
        if (
          duplicateNameTemplateId &&
          !(await confirmReplaceTemplate(data.name))
        ) {
          return;
        }

        const newProject = await promiseSnackbar(
          submitProjectData(data, duplicateNameTemplateId),
        );

        if (newProject) {
          onClose();

          // fetch projects to refresh templates list
          if (newProject.template) fetchProjects();
        }
      } finally {
        onSubmitBusyChange(false);
      }
    },
    [
      isSubmittable,
      type,
      project,
      findDuplicateNameTemplateId,
      confirmReplaceTemplate,
      promiseSnackbar,
      submitProjectData,
      onClose,
      fetchProjects,
      onSubmitBusyChange,
    ],
  );

  return (
    <form onSubmit={handleSubmit(submit)}>
      <DialogContent>
        <Controller
          control={control}
          name="name"
          render={({ field }) => (
            <NameInput field={field} errors={errors} sx={formFieldStyles} />
          )}
        />
        <Controller
          control={control}
          name="description"
          render={({ field }) => (
            <TextField
              {...field}
              autoComplete="off"
              name={generatedDescriptionName}
              label="Description"
              fullWidth
              multiline
              minRows={4}
              sx={formFieldStyles}
              autoFocus={focusField === 'description'}
              onFocus={(e) =>
                focusField === 'description' &&
                is.TextArea(e.target) &&
                e.target.select?.()
              }
            />
          )}
        />
        <Controller
          control={control}
          name="completed_at"
          render={({ field }) => <DateInput field={field} />}
        />
      </DialogContent>
      <EditProjectActions
        project={mode !== 'edit' ? undefined : project}
        disabled={!isSubmittable || isSubmitting}
        cancelDisabled={isSubmitting}
        onClose={onClose}
      />
    </form>
  );
};

const schema = Yup.object({
  name: Yup.string().trim().required('Name is required'),
});

const formFieldStyles: SxProps = { paddingBottom: 4 };

export default EditProjectForm;
